import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AuthCenteredLayout from '@layouts/AuthCenteredLayout/AuthCenteredLayout';
import Input from '@components/Form/Input/Input';
import Button from '@components/Button/Button';
import Icon from '@components/common/Icon';

interface ForgotPasswordFormValues { email: string; }

const ForgotPasswordPage = () => {
    const { t } = useTranslation('auth');
    const navigate = useNavigate();
    const { register, handleSubmit, formState: { errors, isSubmitting } } =
        useForm<ForgotPasswordFormValues>();

    const onSubmit = async (data: ForgotPasswordFormValues) => {
        navigate('/forgot-password/sent', { state: { email: data.email } });
    };

    return (
        <AuthCenteredLayout>
            <div className="bg-white rounded-xl shadow-[var(--shadow-lg)] border border-[var(--neutral-color)] p-[20px] 2xl:p-[30px] flex flex-col items-center">
                <div className="w-10 h-10 2xl:w-14 2xl:h-14 rounded-lg bg-[var(--primary-surface-color)] flex items-center justify-center mb-[20px] 2xl:mb-[30px]">
                    <Icon name="envelope" className="w-auto h-[13px] 2xl:h-[18px]" />
                </div>
                <div className="text-center mb-[20px] 2xl:mb-[30px]">
                    <h1 className="text-[18px] 2xl:text-[24px]  font-semibold text-[var(--secondary-color)]">{t('forgot_password.title')}</h1>
                    <p className="text-[12px] 2xl:text-[14px] text-[var(--disabled-text-color)]">{t('forgot_password.subtitle')}</p>
                </div>
                <form onSubmit={handleSubmit(onSubmit)} className="w-full flex flex-col" noValidate>
                    <Input
                        label={t('forgot_password.email_label')} type="email"
                        placeholder={t('forgot_password.email_placeholder')} required
                        registration={register('email', {
                            required: 'Email is required',
                            pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email' },
                        })}
                        error={errors.email?.message}
                    />
                    <Button className='mt-[10px]' type="submit" variant="primary" fullWidth disabled={isSubmitting}>
                        {t('forgot_password.send_button')}
                    </Button>
                </form>
                <div className="flex items-center gap-3 bg-[var(--primary-canvas-color)] border border-[var(--neutral-color)] rounded-lg p-2 w-full mt-[20px] 2xl:mt-[30px]">
                    <Icon name="infoCircle" size={16} className="shrink-0 mt-0.5" />
                    <p className="text-[11px] 2xl:text-[12px] text-[var(--disabled-text-color)] leading-snug">{t('forgot_password.info')}</p>
                </div>
            </div>
            <div className="flex justify-center mt-5">
                <Button variant="ghost" onClick={() => navigate('/admin/signin')}>
                    <Icon name="arrowLeft" size={14} className="mr-2" />
                    {t('forgot_password.back_to_sign_in')}
                </Button>
            </div>
        </AuthCenteredLayout>
    );
}

export default ForgotPasswordPage;
